import React, { useState } from 'react';
import { LoggedInUserResponse } from '../../store/authStore';
import axiosClient from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const ForgotPassword = () => {
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [resetError, setResetError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetPasswordResponse, setResetPasswordResponse] = useState<string>('');
  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError('');
    setResetError('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setEmailError('Email is required');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setResetError('');

    try {
      const response = await axiosClient.post<LoggedInUserResponse>('/Auth/Reset', {
        email: email.trim(),
      });
      if (response.data.message) {
        setResetPasswordResponse(response.data.message);
        setShowConfirmationModal(true);
      } else {
        setResetError('Failed to send reset email. Please try again.');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Reset password failed:', error);
      setResetError(
        error.response?.data?.message || 'Failed to send reset email. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between gap-4 border-b border-[#2E3835] text-white">
        <img src="/logo.svg" className="w-32 border-r p-4" />
        <a className="border-l p-4 text-sm text-lime" href="/">
          Back to Login
        </a>
      </div>
      <div className="mx-auto mt-[15vh] w-full max-w-[40rem] rounded-md border border-[#2E3835] bg-[#222] shadow-md">
        <div className="border-b border-[#2E3835] p-6">
          <img src="/logo.svg" className="w-32" />
        </div>
        <div className="flex flex-col gap-4 p-4">
          <h1 className="border-b border-[#2E3835] pb-4 text-xl font-bold text-white">
            Reset Password
          </h1>

          <form onSubmit={handleForgotPassword}>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                autoFocus
                id="email"
                type="email"
                className="mt-2 w-full border-[#2E3835] bg-inherit p-2 text-white shadow-md focus:border-green-500 focus:outline-none"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                required
                disabled={isLoading}
              />
              {emailError && <p className="mt-1 text-sm text-red-500">{emailError}</p>}
            </div>

            {resetError && (
              <div className="my-4 rounded-md border border-red-500/20 bg-red-500/10 p-3">
                <p className="text-sm text-red-500">{resetError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="mt-4 w-full bg-lime text-black"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <>
                  Sending Reset Email...
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>

          <Dialog
            open={showConfirmationModal && !emailError}
            onOpenChange={setShowConfirmationModal}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Password Reset</DialogTitle>
                <DialogDescription>
                  {resetPasswordResponse}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    navigate('/login');
                  }}
                >
                  Back to Login
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
